import { useAbility } from '@casl/react';
import { ArrowForward, WarningOutlined } from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Grid,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { Popup } from 'react-leaflet';
import { useNavigate } from 'react-router-dom';
import { ACTIONS } from 'src/auth/utils/defineAbilityFor';
import { AbilityContext } from 'src/context/canContext';
import { useDashboard } from 'src/context/dashboardContext';
import { onlineStatusColor } from 'src/features/dashboard/utils/onlineStatus';
import { useAdminUrlNavigation } from 'src/hooks/useAdminUrlNavigation';
import { useFetchProviderQuery } from 'src/services/switch-rest-api/provider.service';
import { Alert } from 'switch-shared/modules/crud/alert/alert.entity';
import {
  AppGroupContactDetails,
  ApplicationGroup,
} from 'switch-shared/modules/crud/application-group/application-group.entity';
import { ContactType } from 'switch-shared/modules/crud/application-group/application-group.enums';
import { Application } from 'switch-shared/modules/crud/application/application.entity';
import { UtilityType } from 'switch-shared/modules/crud/utility/utility.enums';

import './CustomPopup.css';

/** Online and offline meter counts for one application. */
export interface MeterStatus {
  online: number;
  offline: number;
}

interface Props {
  application: Application;

  group?: ApplicationGroup;

  /** Active alerts scoped to the application. */
  alerts: Alert[];

  /** Present only when meters were loaded for the map. */
  meterStatus?: MeterStatus;
}

const ALERT_LIMIT = 3;

const UTILITY_LABELS: Record<UtilityType, string> = {
  [UtilityType.ELECTRICITY]: 'Electricity',
  [UtilityType.WATER]: 'Water',
  [UtilityType.GAS]: 'Gas',
  [UtilityType.FIXED_FEE]: 'Fixed fee',
};

const PROVIDER_FIELDS: Partial<
  Record<UtilityType, 'electricityProvider' | 'waterProvider' | 'gasProvider'>
> = {
  [UtilityType.ELECTRICITY]: 'electricityProvider',
  [UtilityType.WATER]: 'waterProvider',
  [UtilityType.GAS]: 'gasProvider',
};

/** The primary contact in `contacts`, else the first one. */
const primaryContact = (
  contacts?: AppGroupContactDetails[]
): AppGroupContactDetails | undefined =>
  contacts?.find((contact) => contact.type === ContactType.Primary) ??
  contacts?.[0];

const SectionLabel = ({ children }: { children: string }) => {
  const theme = useTheme();
  return (
    <Typography variant='h5' sx={{ color: theme.colors.primary.main }}>
      {children}
    </Typography>
  );
};

const ProviderLine = ({
  utility,
  providerId,
}: {
  utility: UtilityType;
  providerId?: string;
}) => {
  const ability = useAbility(AbilityContext);
  const { data: provider, isLoading } = useFetchProviderQuery(
    { id: providerId! },
    { skip: !providerId || ability.cannot(ACTIONS.READ, 'Providers') }
  );

  const name = !providerId
    ? 'Not set'
    : isLoading
      ? 'Loading…'
      : (provider?.name ?? 'Unknown');

  return (
    <Typography variant='subtitle1'>
      {UTILITY_LABELS[utility]}: {name}
    </Typography>
  );
};

const ApplicationPopUp = ({
  application,
  group,
  alerts,
  meterStatus,
}: Props) => {
  /* Hooks & Use Effects */
  const theme = useTheme();
  const navigate = useNavigate();
  const { getApplicationPath } = useAdminUrlNavigation();
  const { availableUtilities } = useDashboard();

  const appContact = primaryContact(application.contactDetails);
  const contact =
    appContact ?? primaryContact(group?.clientDetails?.contactDetails);
  const contactName = [contact?.name, contact?.surname]
    .filter(Boolean)
    .join(' ');

  const providerUtilities = (availableUtilities ?? []).filter(
    (utility) => PROVIDER_FIELDS[utility]
  );

  const meterTotal = meterStatus ? meterStatus.online + meterStatus.offline : 0;

  return (
    <Popup>
      <Card
        sx={{
          minWidth: 320,
          maxWidth: 420,
          backgroundColor: 'rgba(0,0,0,0)',
          borderRadius: '0',
        }}
      >
        <CardHeader
          title={application.name}
          titleTypographyProps={{
            variant: 'h3',
            fontWeight: 'bold',
            lineHeight: '1.2',
            sx: { color: theme.colors.primary.main },
          }}
          action={
            <IconButton
              color='primary'
              title='Open application'
              style={{ position: 'absolute', right: 0 }}
              onClick={() =>
                navigate(
                  getApplicationPath(
                    application._id,
                    application.applicationGroupId
                  )
                )
              }
            >
              <ArrowForward />
            </IconButton>
          }
          sx={{ padding: 0, position: 'relative', paddingRight: 8 }}
        />

        <Stack
          direction='row'
          spacing={1}
          sx={{ mb: 2, mt: 1 }}
          flexWrap='wrap'
          useFlexGap
        >
          <Chip
            label='Application'
            size='small'
            sx={{ backgroundColor: theme.colors.neutral.grey }}
          />
          {meterStatus && (
            <Tooltip
              title={`${meterStatus.online} of ${meterTotal} meters online`}
            >
              <Chip
                label={
                  meterTotal > 0
                    ? `${Math.round((meterStatus.online / meterTotal) * 100)}% online`
                    : 'No meters'
                }
                size='small'
                sx={{
                  backgroundColor: onlineStatusColor(
                    theme,
                    meterStatus.online,
                    meterTotal
                  ),
                  color: theme.palette.common.white,
                  fontWeight: theme.tokens.typography.fontWeight.semibold,
                  cursor: 'default',
                }}
              />
            </Tooltip>
          )}
        </Stack>

        <CardContent sx={{ padding: 0, paddingBottom: '0 !important' }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <SectionLabel>Address</SectionLabel>
              <Typography variant='subtitle1'>
                {application.location?.address ?? 'N/A'}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <SectionLabel>
                {appContact ? 'Primary Contact' : 'Group Contact'}
              </SectionLabel>
              {contact ? (
                <Box>
                  {contactName && (
                    <Typography variant='subtitle1'>{contactName}</Typography>
                  )}
                  {contact.phone && (
                    <Link
                      variant='subtitle1'
                      display='block'
                      href={`tel:${contact.phone}`}
                    >
                      {contact.phone}
                    </Link>
                  )}
                  {contact.email && (
                    <Link
                      variant='subtitle1'
                      display='block'
                      href={`mailto:${contact.email}`}
                      sx={{ wordBreak: 'break-all' }}
                    >
                      {contact.email}
                    </Link>
                  )}
                </Box>
              ) : (
                <Typography variant='subtitle1'>None</Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <SectionLabel>Providers</SectionLabel>
              {providerUtilities.map((utility) => (
                <ProviderLine
                  key={utility}
                  utility={utility}
                  providerId={
                    application[PROVIDER_FIELDS[utility]!] as unknown as
                      | string
                      | undefined
                  }
                />
              ))}
            </Grid>

            <Grid item xs={12}>
              <SectionLabel>
                {`Active Alerts${alerts.length > 0 ? ` (${alerts.length})` : ''}`}
              </SectionLabel>
              {alerts.length === 0 ? (
                <Typography variant='subtitle1'>None</Typography>
              ) : (
                <List dense sx={{ padding: 0 }}>
                  {alerts.slice(0, ALERT_LIMIT).map((alert) => (
                    <ListItem key={alert._id} sx={{ padding: 0 }}>
                      <WarningOutlined
                        color='warning'
                        fontSize='small'
                        sx={{ mr: 0.5 }}
                      />
                      <ListItemText
                        primary={alert.cause.description}
                        primaryTypographyProps={{ variant: 'h5' }}
                      />
                    </ListItem>
                  ))}
                  {alerts.length > ALERT_LIMIT && (
                    <ListItem sx={{ padding: 0 }}>
                      <ListItemText
                        primary={`and ${alerts.length - ALERT_LIMIT} more...`}
                        primaryTypographyProps={{ variant: 'h5' }}
                      />
                    </ListItem>
                  )}
                </List>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Popup>
  );
};

export default ApplicationPopUp;
